import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { PLANS, PRICE_IDS } from '@/features/subscription/plans';
import type { TierKey } from '@/features/subscription/hooks/useSubscription';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  showToggle?: boolean;
  defaultYearly?: boolean;
  onSelect?: (plan: typeof PLANS[number]) => void;
  tier: TierKey;
  isLegacyFree: boolean;
  trialAvailable?: boolean;
  startCheckout: (priceId: string) => Promise<void>;
  manageSubscription: () => Promise<void>;
  subscriptionEnd?: string | null;
  subscriptionStatus?: string | null;
  billingInterval?: 'month' | 'year' | null;
  trialEnd?: string | null;
}

export function PlanCards({
  showToggle = true,
  defaultYearly = false,
  onSelect,
  tier,
  isLegacyFree,
  trialAvailable = true,
  startCheckout,
  manageSubscription,
  subscriptionEnd,
  subscriptionStatus,
  billingInterval,
  trialEnd,
}: Props) {
  const [isYearly, setIsYearly] = useState(defaultYearly);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const hasActiveSub = tier !== 'free' && tier !== 'trial' && !isLegacyFree;

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleSelect = async (plan: typeof PLANS[number]) => {
    const priceId = isYearly ? PRICE_IDS[plan.id].yearly : PRICE_IDS[plan.id].monthly;
    if (!priceId) { toast.info("Ce plan n'est pas encore disponible. Contactez-nous."); return; }
    setLoading(true);
    try {
      await startCheckout(priceId);
      onSelect?.(plan);
    } catch {
      toast.error('Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {showToggle && (
        <div className="flex items-center justify-center gap-3">
          <span
            className="text-sm font-medium cursor-pointer"
            style={{ color: !isYearly ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}
            onClick={() => setIsYearly(false)}
          >
            Mensuel
          </span>
          <div
            onClick={() => setIsYearly(!isYearly)}
            className="relative cursor-pointer"
            style={{
              width: '44px', height: '24px', borderRadius: '12px',
              background: isYearly ? '#1D9E75' : '#4B5563',
              transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute', top: '3px',
              left: isYearly ? '23px' : '3px',
              width: '18px', height: '18px',
              borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s',
            }} />
          </div>
          <span
            className="text-sm font-medium cursor-pointer"
            style={{ color: isYearly ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}
            onClick={() => setIsYearly(true)}
          >
            Annuel
          </span>
          <span style={{
            fontSize: '11px', fontWeight: 500,
            padding: '2px 8px', borderRadius: '20px',
            background: '#E1F5EE', color: '#085041',
          }}>
            2 mois offerts
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((plan, i) => {
          const isCurrent = plan.tierKey === tier;
          const isActiveSubscriber = isCurrent && !isLegacyFree;
          const price = isYearly ? Math.round(plan.yearlyPrice / 12) : plan.monthlyPrice;
          const yearlyTotal = plan.yearlyPrice;

          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className={`relative flex flex-col rounded-2xl border-2 p-5 gap-3 ${
                isActiveSubscriber
                  ? 'border-teal-500 shadow-lg shadow-teal-500/20 bg-teal-500/5'
                  : plan.highlighted
                    ? 'border-teal-500 shadow-md shadow-teal-500/10 bg-teal-500/5'
                    : 'border-border bg-card'
              }`}
            >
              {plan.badge && !isActiveSubscriber && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-teal-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              )}
              {isActiveSubscriber && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full whitespace-nowrap">
                  Plan actuel
                </span>
              )}

              <div>
                <h3 className={`text-lg font-bold font-display ${plan.highlighted || isActiveSubscriber ? 'text-teal-600 dark:text-teal-400' : ''}`}>
                  {plan.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
              </div>

              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold font-display">{price}€</span>
                  <span className="text-muted-foreground text-sm">/ mois</span>
                </div>
                <p className="text-[11px] font-medium mt-0.5" style={{ color: '#1D9E75' }}>
                  {isYearly
                    ? `Facturé ${yearlyTotal}€ / an`
                    : `ou ${plan.monthlyEquivalent}€/mois en annuel`}
                </p>
              </div>

              <ul className="space-y-1.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs">
                    <Check size={13} className={`mt-0.5 shrink-0 ${plan.highlighted || isActiveSubscriber ? 'text-teal-500' : 'text-primary'}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {isActiveSubscriber ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                  >
                    Voir les détails
                  </button>
                  <button
                    onClick={() => manageSubscription()}
                    className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
                  >
                    Gérer
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleSelect(plan)}
                  disabled={loading}
                  className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 ${
                    plan.highlighted
                      ? 'bg-teal-500 text-white hover:bg-teal-600'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                >
                  {hasActiveSub ? 'Changer pour ce plan' : trialAvailable ? plan.cta : "S'abonner maintenant"}
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Détails de votre abonnement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Statut</span>
              {subscriptionStatus === 'trialing' ? (
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/15 text-blue-600 dark:text-blue-400">Essai gratuit</span>
              ) : subscriptionStatus === 'active' ? (
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">Actif</span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground capitalize">{subscriptionStatus ?? '—'}</span>
              )}
            </div>

            {billingInterval && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Facturation</span>
                <span className="text-sm font-medium">{billingInterval === 'year' ? 'Annuelle' : 'Mensuelle'}</span>
              </div>
            )}

            {subscriptionEnd && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {subscriptionStatus === 'trialing' ? "Essai jusqu'au" : 'Renouvellement le'}
                </span>
                <span className="text-sm font-medium">
                  {new Date(subscriptionEnd).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            )}

            {subscriptionStatus === 'trialing' && trialEnd && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <span className="text-lg leading-none">🎁</span>
                <div>
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">Essai gratuit en cours</p>
                  <p className="text-xs text-blue-600/80 dark:text-blue-400/80">
                    Aucun prélèvement avant le {new Date(trialEnd).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            )}

            {billingInterval === 'month' && subscriptionStatus === 'active' && (
              <div className="p-3 rounded-xl bg-primary/5 border border-primary/10 space-y-2">
                <p className="text-sm font-semibold">Passer à l'annuel et économiser 2 mois</p>
                <p className="text-xs text-muted-foreground">Économisez l'équivalent de 2 mensualités en passant à la facturation annuelle.</p>
                <button
                  onClick={() => { manageSubscription(); setShowDetails(false); }}
                  className="w-full py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-all"
                >
                  Changer pour l'annuel →
                </button>
              </div>
            )}

            <button
              onClick={() => setShowDetails(false)}
              className="w-full py-2.5 rounded-xl font-semibold text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-all"
            >
              Fermer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <p className="text-center text-xs text-muted-foreground">
        {trialAvailable
          ? "Essai gratuit 14 jours · Aucun prélèvement pendant l'essai · Annulable à tout moment"
          : 'Paiement immédiat · Annulable à tout moment'}
      </p>
    </div>
  );
}
